// API Base Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5002/api';

// Token management
const getToken = (): string | null => {
  return localStorage.getItem('access_token');
};

const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
};

const clearTokens = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// API Request helper
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Important pour CORS avec cookies/sessions
  });

  const isAuthEndpoint = endpoint.startsWith('/auth/login') || 
                         endpoint.startsWith('/auth/register') || 
                         endpoint.startsWith('/auth/refresh') ||
                         endpoint.startsWith('/auth/forgot') ||
                         endpoint.startsWith('/auth/reset');

  if (response.status === 401 && !isAuthEndpoint) {
    // Try to refresh token
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${refreshToken}`,
          },
        });
        
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          localStorage.setItem('access_token', data.access_token);
          return apiRequest(endpoint, options);
        }
      } catch {
        clearTokens();
        window.location.href = '/login';
      }
    }
    clearTokens();
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed', details: '' }));
    const err = new Error(error.error || 'Request failed') as any;
    err.response = { data: error };
    throw err;
  }

  return response.json();
}

// Types
export interface User {
  id: number;
  name: string;
  email: string;
  status: 'active' | 'suspended';
  api_calls: number;
  total_spent: number;
  stripe_customer_id?: string;
  last_login?: string;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  email_verified?: boolean;
  two_factor_enabled?: boolean;
  // User preferences
  user_type?: string;
  company_name?: string;
  company_vat?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  source_language?: string;
  target_language?: string;
  email_notifications?: boolean;
  quota_alerts?: boolean;
  marketing_emails?: boolean;
}

export interface ApiKey {
  id: number;
  name: string;
  key: string;
  user_id: number;
  status: 'active' | 'revoked';
  last_used?: string;
  created_at: string;
  updated_at: string;
}

export interface Payment {
  id: number;
  user_id: number;
  invoice_id: string;
  amount: number;
  currency: string;
  plan: string;
  status: 'pending' | 'paid' | 'failed';
  paid_at?: string;
  created_at: string;
}

export interface LoginResponse {
  message: string;
  access_token?: string;
  refresh_token?: string;
  user?: User;
  two_factor_required?: boolean;
  email?: string;
}

export interface UsersResponse {
  users: User[];
}

export interface UserResponse {
  user: User;
}

export interface ApiKeysResponse {
  api_keys: ApiKey[];
}

export interface ApiKeyResponse {
  message: string;
  api_key: ApiKey;
}

// Auth API
export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await apiRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setTokens(response.access_token, response.refresh_token);
    return response;
  },

  register: async (name: string, email: string, password: string): Promise<LoginResponse> => {
    const response = await apiRequest<LoginResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
    setTokens(response.access_token, response.refresh_token);
    return response;
  },

  logout: async (): Promise<void> => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } finally {
      clearTokens();
    }
  },

  getCurrentUser: async (): Promise<UserResponse> => {
    return apiRequest<UserResponse>('/auth/me');
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    return apiRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    return apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    return apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  verifyEmail: async (payload: { token: string; email?: string }): Promise<{ message: string; user: User }> => {
    return apiRequest('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  resendVerification: async (): Promise<{ message: string }> => {
    return apiRequest('/auth/resend-verification', {
      method: 'POST',
    });
  },

  resendVerificationPublic: async (email: string): Promise<{ message: string }> => {
    return apiRequest('/auth/resend-verification-public', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Two-Factor Authentication
  verifyTwoFactor: async (email: string, code: string): Promise<LoginResponse> => {
    const response = await apiRequest<LoginResponse>('/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    if (response.access_token && response.refresh_token) {
      setTokens(response.access_token, response.refresh_token);
    }
    return response;
  },

  enableTwoFactor: async (): Promise<{ message: string; two_factor_pending: boolean }> => {
    return apiRequest('/auth/enable-2fa', {
      method: 'POST',
    });
  },

  confirmTwoFactor: async (code: string): Promise<{ message: string; two_factor_enabled: boolean }> => {
    return apiRequest('/auth/confirm-2fa', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  disableTwoFactor: async (): Promise<{ message: string; two_factor_enabled: boolean }> => {
    return apiRequest('/auth/disable-2fa', {
      method: 'POST',
    });
  },

  getTwoFactorStatus: async (): Promise<{ two_factor_enabled: boolean }> => {
    return apiRequest('/auth/2fa-status');
  },
};

// Users API (Admin)
export const usersApi = {
  getAll: async (): Promise<UsersResponse> => {
    return apiRequest<UsersResponse>('/users/users');
  },

  create: async (data: {
    name: string;
    email: string;
    password: string;
    is_admin?: boolean;
  }): Promise<{ message: string; user: User }> => {
    return apiRequest('/users/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getById: async (userId: number): Promise<UserResponse> => {
    return apiRequest<UserResponse>(`/users/users/${userId}`);
  },

  update: async (userId: number, data: Partial<User>): Promise<{ message: string; user: User }> => {
    return apiRequest(`/users/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (userId: number): Promise<{ message: string }> => {
    return apiRequest(`/users/users/${userId}`, {
      method: 'DELETE',
    });
  },

  toggleStatus: async (userId: number): Promise<{ message: string; user: User }> => {
    return apiRequest(`/users/users/${userId}/toggle-status`, {
      method: 'PUT',
    });
  },

  getApiKeys: async (userId: number): Promise<ApiKeysResponse> => {
    return apiRequest<ApiKeysResponse>(`/users/users/${userId}/api-keys`);
  },

  getUsage: async (userId: number): Promise<{
    user_id: number;
    current_month_usage: {
      api_calls: number;
      characters: number;
      quota_limit: number;
      quota_used: number;
      quota_percentage: number;
    };
    providers: Array<{
      provider: string;
      calls: number;
      characters: number;
      cost: number;
    }>;
    credit_balance: number;
    total_credits_purchased: number;
  }> => {
    return apiRequest(`/users/users/${userId}/usage`);
  },

  getUsageTimeline: async (userId: number, period: '7days' | '30days' | '90days' = '7days'): Promise<{
    timeline: Array<{
      date: string;
      calls: number;
      translate: number;
      ocr: number;
      detect: number;
    }>;
  }> => {
    return apiRequest(`/users/users/${userId}/usage/timeline?period=${period}`);
  },

  getQuota: async (): Promise<{
    user_id: number;
    quota_limit: number;
    monthly_used: number;
    quota_remaining: number;
    can_translate_today: boolean;
    quota_percentage: number;
    credit_balance: number;
    total_credits_purchased: number;
  }> => {
    return apiRequest('/users/me/quota');
  },

  getPublicSettings: async (): Promise<{
    settings: {
      max_text_length: number;
      max_file_size_mb: number;
      free_plan_daily_limit: number;
      starter_plan_daily_limit: number;
      professional_plan_daily_limit: number;
      enterprise_plan_daily_limit: number;
      app_name: string;
      app_version: string;
    };
  }> => {
    return apiRequest('/users/settings/public');
  },

  // Profile management
  getProfile: async (): Promise<{
    user: User & {
      user_type?: string;
      company_name?: string;
      company_vat?: string;
      address_line1?: string;
      address_line2?: string;
      city?: string;
      postal_code?: string;
      country?: string;
      phone?: string;
      source_language?: string;
      target_language?: string;
      email_notifications?: boolean;
      quota_alerts?: boolean;
      marketing_emails?: boolean;
    };
  }> => {
    return apiRequest('/users/me/profile');
  },

  updateProfile: async (data: {
    name?: string;
    email?: string;
    user_type?: string;
    company_name?: string;
    company_vat?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    phone?: string;
    source_language?: string;
    target_language?: string;
    email_notifications?: boolean;
    quota_alerts?: boolean;
    marketing_emails?: boolean;
  }): Promise<{
    message: string;
    user: User & {
      user_type?: string;
      company_name?: string;
      company_vat?: string;
      address_line1?: string;
      address_line2?: string;
      city?: string;
      postal_code?: string;
      country?: string;
      phone?: string;
      source_language?: string;
      target_language?: string;
      email_notifications?: boolean;
      quota_alerts?: boolean;
      marketing_emails?: boolean;
    };
  }> => {
    return apiRequest('/users/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// API Keys API
export const apiKeysApi = {
  getAll: async (): Promise<ApiKeysResponse> => {
    return apiRequest<ApiKeysResponse>('/api-keys/api-keys');
  },

  create: async (name: string): Promise<ApiKeyResponse> => {
    return apiRequest('/api-keys/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  update: async (keyId: number, name: string): Promise<ApiKeyResponse> => {
    return apiRequest(`/api-keys/api-keys/${keyId}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },

  delete: async (keyId: number): Promise<{ message: string }> => {
    return apiRequest(`/api-keys/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  },

  revoke: async (keyId: number): Promise<ApiKeyResponse> => {
    return apiRequest(`/api-keys/api-keys/${keyId}/revoke`, {
      method: 'PUT',
    });
  },

  activate: async (keyId: number): Promise<ApiKeyResponse> => {
    return apiRequest(`/api-keys/api-keys/${keyId}/activate`, {
      method: 'PUT',
    });
  },

  // Admin endpoints
  getAllAdmin: async (): Promise<ApiKeysResponse> => {
    return apiRequest<ApiKeysResponse>('/admin/api-keys');
  },

  deleteAdmin: async (keyId: number): Promise<{ message: string }> => {
    return apiRequest(`/admin/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  },
};

// Translations API
export const translationsApi = {
  translate: async (data: {
    text: string;
    source_language?: string;
    target_language: string;
    provider?: string;
  }): Promise<{
    translated_text: string;
    source_language: string;
    target_language: string;
    characters_count: number;
    translation_time?: number;
    provider?: string;
  }> => {
    const response = await apiRequest<{
      success: boolean;
      translation: {
        translated_text: string;
        source_language: string;
        target_language: string;
        characters_count: number;
      };
      provider: string;
      translation_time?: number;
    }>('/translations/translate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    // Extract the translation data from the nested structure
    return {
      translated_text: response.translation.translated_text,
      source_language: response.translation.source_language,
      target_language: response.translation.target_language,
      characters_count: response.translation.characters_count,
      translation_time: response.translation_time,
      provider: response.provider,
    };
  },

  translateImage: async (imageFile: File, targetLanguage: string, sourceLanguage?: string): Promise<{
    success: boolean;
    translation: {
      id: number;
      source_text: string;
      translated_text: string;
      source_language: string;
      target_language: string;
      characters_count: number;
      created_at: string;
    };
    provider: string;
    extracted_text_preview: string;
  }> => {
    const token = getToken();
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('target_language', targetLanguage);
    if (sourceLanguage) {
      formData.append('source_language', sourceLanguage);
    }

    const response = await fetch(`${API_BASE_URL}/translations/translate/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Image translation failed');
    }

    return response.json();
  },

  getHistory: async (page: number = 1, limit: number = 20): Promise<{
    translations: Array<{
      id: number;
      source_text: string;
      translated_text: string;
      source_language: string;
      target_language: string;
      created_at: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await apiRequest<{
      translations: Array<{
        id: number;
        source_text: string;
        translated_text: string;
        source_language: string;
        target_language: string;
        created_at: string;
      }>;
      pagination: {
        page: number;
        per_page: number;
        total: number;
        pages: number;
      };
    }>(`/translations/?page=${page}&per_page=${limit}`);
    
    // Transform the response to match the expected format
    return {
      translations: response.translations,
      total: response.pagination.total,
      page: response.pagination.page,
      limit: response.pagination.per_page
    };
  },
};

// Billing API
export const billingApi = {
  getPayments: async (): Promise<{ payments: Payment[] }> => {
    return apiRequest('/billing/payments');
  },

  getUserPayments: async (userId: number): Promise<{ payments: Payment[] }> => {
    return apiRequest(`/billing/users/${userId}/payments`);
  },

  createPaymentIntent: async (amount: number): Promise<{
    client_secret: string;
    publishable_key: string;
    amount: number;
  }> => {
    return apiRequest('/billing/create-payment-intent', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },

  confirmPaymentCredit: async (amount: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    return apiRequest('/billing/confirm-credit', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },

  createCheckoutSession: async (amount: number): Promise<{
    checkout_url: string;
    session_id: string;
  }> => {
    return apiRequest('/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },

  getAvailablePaymentMethods: async (): Promise<{
    payment_methods: {
      stripe: { enabled: boolean };
      paypal: { enabled: boolean };
      bank_transfer: {
        enabled: boolean;
        bank_name?: string;
        account_name?: string;
        account_number?: string;
        iban?: string;
        swift_bic?: string;
        instructions?: string;
      };
      satim_dz: { enabled: boolean };
      payoneer: { enabled: boolean };
    };
  }> => {
    return apiRequest('/billing/payment-methods');
  },

  getCreditPacks: async (): Promise<{
    credit_packs: Array<{
      id: number;
      name: string;
      amount: number;
      is_active: boolean;
      color: string;
      priority: number;
    }>;
  }> => {
    return apiRequest('/billing/credit-packs');
  },

  getProviderRates: async (): Promise<{
    providers: Array<{
      id: number;
      name: string;
      price_input?: number;
    }>;
  }> => {
    return apiRequest('/billing/providers');
  },

  getInvoices: async (): Promise<{
    invoices: Array<{
      id: number;
      invoice_id: string;
      amount: number;
      currency: string;
      plan: string;
      status: 'pending' | 'paid' | 'failed';
      paid_at?: string;
      created_at: string;
    }>;
  }> => {
    return apiRequest('/billing/invoices');
  },

  downloadInvoice: async (paymentId: number): Promise<Blob> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/billing/invoices/${paymentId}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to download invoice');
    }
    
    return response.blob();
  },

  downloadBankTransferInvoice: async (transferId: number): Promise<Blob> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/billing/bank-transfers/${transferId}/invoice`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to download bank transfer invoice');
    }
    
    return response.blob();
  },

  // PayPal
  createPayPalOrder: async (creditAmount: number): Promise<{
    success: boolean;
    order_id: string;
    approval_url: string;
    amount: number;
  }> => {
    return apiRequest('/billing/paypal/create-order', {
      method: 'POST',
      body: JSON.stringify({ amount: creditAmount }),
    });
  },

  capturePayPalOrder: async (orderId: string): Promise<{
    success: boolean;
    message: string;
    credit_amount: number;
    payment_id: number;
  }> => {
    return apiRequest('/billing/paypal/capture', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId }),
    });
  },

  // Bank Transfer
  requestBankTransfer: async (creditAmount: number, reference: string): Promise<{
    success: boolean;
    bank_transfer: any;
    message: string;
  }> => {
    return apiRequest('/billing/bank-transfer/request', {
      method: 'POST',
      body: JSON.stringify({ amount: creditAmount, reference }),
    });
  },

  // SATIM
  createSatimOrder: async (creditPackId: number, creditAmount: number): Promise<{
    success: boolean;
    order_id: string;
    order_number: string;
    payment_url: string;
    payment_id: number;
  }> => {
    return apiRequest('/billing/satim/create-order', {
      method: 'POST',
      body: JSON.stringify({ pack_id: creditPackId, amount: creditAmount }),
    });
  },

  verifySatimPayment: async (orderId: string): Promise<{
    success: boolean;
    order_status?: number;
    payment_status?: string;
  }> => {
    return apiRequest('/billing/satim/verify', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId }),
    });
  },

   verifySatimPaymentByOrder: async (orderNumber: string): Promise<{
     success: boolean;
     order_status?: number;
     payment_status?: string;
   }> => {
     return apiRequest('/billing/satim/verify-by-order', {
       method: 'POST',
       body: JSON.stringify({ order_number: orderNumber }),
     });
   },

   verifyPayoneerPayment: async (paymentId?: string, orderNumber?: string): Promise<{
     success: boolean;
     status?: string;
     payoneer_status?: string;
     amount?: number;
     currency?: string;
     error?: string;
   }> => {
     return apiRequest('/billing/payoneer/verify', {
       method: 'POST',
       body: JSON.stringify({ payment_id: paymentId, order_number: orderNumber }),
     });
   },

  simulateSatimPayment: async (cardNumber: string, expiry: string, cvv: string, orderNumber: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    errorCode?: string;
  }> => {
    return apiRequest('/billing/satim/simulate', {
      method: 'POST',
      body: JSON.stringify({ card_number: cardNumber, expiry, cvv, order_number: orderNumber }),
    });
  },

   getSatimConfig: async (): Promise<{
     enabled: boolean;
     active_mode: string;
   }> => {
     return apiRequest('/billing/satim/config');
   },

   createPayoneerOrder: async (amount: number, plan?: string): Promise<{
     success: boolean;
     order_id?: string;
     order_number?: string;
     payment_url?: string;
     payment_id?: number;
     error?: string;
   }> => {
     return apiRequest('/billing/payoneer/create-order', {
       method: 'POST',
       body: JSON.stringify({ amount, plan }),
     });
   },

   getBankTransfers: async (): Promise<{
    bank_transfers: any[];
  }> => {
    return apiRequest('/billing/bank-transfer/status');
  },

  upgradePlan: async (creditAmount: number): Promise<{
    success: boolean;
    client_secret?: string;
    payment_id?: number;
    credit_amount: number;
    error?: string;
  }> => {
    return apiRequest('/billing/upgrade', {
      method: 'POST',
      body: JSON.stringify({ amount: creditAmount }),
    });
  },

  subscribe: async (creditAmount: number): Promise<{
    success: boolean;
    subscription_id?: string;
    client_secret?: string;
    credit_amount: number;
    error?: string;
  }> => {
    return apiRequest('/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId, billing_cycle: billingCycle }),
    });
  },

  cancelSubscription: async (): Promise<{
    message: string;
  }> => {
    return apiRequest('/billing/cancel-subscription', {
      method: 'POST',
    });
  },

  applyPromoCode: async (code: string): Promise<{
    success: boolean;
    discount_percent?: number;
    discount_amount?: number;
    message: string;
  }> => {
    return apiRequest('/billing/apply-promo', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  getBillingSummary: async (): Promise<{
    plan: string;
    monthly_price: number;
    extra_usage: number;
    discount: number;
    discount_type?: 'percent' | 'fixed';
    promo_code?: string;
    total: number;
    billing_cycle: 'monthly' | 'annual';
    next_renewal_date: string;
  }> => {
    return apiRequest('/billing/summary');
  },

  savePaymentMethod: async (data: {
    payment_method: 'card' | 'paypal';
    card_name?: string;
    card_number?: string;
    card_expiry?: string;
    card_cvc?: string;
  }): Promise<{
    success: boolean;
    message: string;
    payment_method_id?: string;
  }> => {
    return apiRequest('/billing/payment-method', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Admin API
export const adminApi = {
  getStats: async (): Promise<{
    statistics: {
      total_users: number;
      active_users: number;
      new_users_month: number;
      total_translations: number;
      month_translations: number;
      total_revenue: number;
      month_revenue: number;
    };
    plan_distribution: Record<string, number>;
    recent_users: Array<{
      id: number;
      name: string;
      email: string;
      plan: string;
      status: string;
      created_at: string;
    }>;
    recent_payments: Array<{
      id: number;
      user_id: number;
      amount: number;
      plan: string;
      paid_at: string | null;
    }>;
    provider_revenue: Array<{
      provider: string;
      revenue: number;
      cost: number;
      margin: number;
      translations: number;
      characters: number;
    }>;
  }> => {
    return apiRequest('/admin/dashboard');
  },

  getAiProviders: async (): Promise<{
    providers: Array<{
      id: number;
      name: string;
      api_key: string;
      status: string;
      priority: number;
      test_status: string;
      last_tested?: string;
      created_at?: string;
      updated_at?: string;
      balance?: number;
      balance_updated_at?: string;
      cost_input?: number;
      cost_output?: number;
      price_input?: number;
      price_output?: number;
      currency?: string;
    }>;
  }> => {
    return apiRequest('/admin/ai-providers');
  },

  createAiProvider: async (data: {
    name: string;
    api_key: string;
    status?: string;
    priority?: number;
    balance?: number;
    cost_input?: number;
    cost_output?: number;
    price_input?: number;
    price_output?: number;
    currency?: string;
  }): Promise<{ message: string }> => {
    return apiRequest('/admin/ai-providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateAiProvider: async (providerId: number, data: {
    api_key?: string;
    status?: string;
    priority?: number;
    balance?: number;
    cost_input?: number;
    cost_output?: number;
    price_input?: number;
    price_output?: number;
    currency?: string;
  }): Promise<{ message: string }> => {
    return apiRequest(`/admin/ai-providers/${providerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteAiProvider: async (providerId: number): Promise<{ message: string }> => {
    return apiRequest(`/admin/ai-providers/${providerId}`, {
      method: 'DELETE',
    });
  },

  testAiProvider: async (providerId: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    return apiRequest(`/admin/ai-providers/${providerId}/test`, {
      method: 'POST',
    });
  },

  getCreditPacks: async (): Promise<{
    credit_packs: Array<{
      id: number;
      name: string;
      amount: number;
      is_active: boolean;
      color: string;
      priority: number;
    }>;
  }> => {
    return apiRequest('/admin/credit-packs');
  },

  updateCreditPack: async (packId: number, data: {
    amount?: number;
    name?: string;
    is_active?: boolean;
    color?: string;
    priority?: number;
  }): Promise<{ message: string; credit_pack: any }> => {
    return apiRequest(`/admin/credit-packs/${packId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getSettings: async (): Promise<{
    settings: Record<string, string>;
  }> => {
    return apiRequest('/admin/settings');
  },

  updateSetting: async (key: string | Record<string, string>, value?: string): Promise<{ message: string }> => {
    let body;
    if (typeof key === 'string' && value !== undefined) {
      // Single key-value pair
      body = JSON.stringify({ [key]: value });
    } else if (typeof key === 'object') {
      // Dictionary of settings
      body = JSON.stringify(key);
    } else {
      throw new Error('Invalid arguments for updateSetting');
    }
    
    return apiRequest('/admin/settings', {
      method: 'POST',
      body,
    });
  },

  createApiKeyForUser: async (userId: string | number, name: string): Promise<{
    message: string;
    api_key: ApiKey;
  }> => {
    return apiRequest('/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, name }),
    });
  },

  revokeApiKey: async (keyId: number): Promise<{
    message: string;
    api_key: ApiKey;
  }> => {
    return apiRequest(`/admin/api-keys/${keyId}/revoke`, {
      method: 'PUT',
    });
  },

  updateProviderBalance: async (providerId: number): Promise<{
    success: boolean;
    message: string;
    balance: number;
    error?: string;
  }> => {
    return apiRequest(`/admin/ai-providers/${providerId}/update-balance`, {
      method: 'POST',
    });
  },

  // reCAPTCHA
  getRecaptchaSettings: async (): Promise<{
    enabled: boolean;
    site_key: string;
    secret_key: string;
  }> => {
    return apiRequest('/admin/recaptcha');
  },

  updateRecaptchaSettings: async (data: {
    enabled: boolean;
    site_key: string;
    secret_key: string;
  }): Promise<{ message: string }> => {
    return apiRequest('/admin/recaptcha', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getRecaptchaPublicSettings: async (): Promise<{
    enabled: boolean;
    site_key: string;
  }> => {
    return apiRequest('/admin/recaptcha/public');
  },

  testSmtp: async (testEmail?: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    return apiRequest('/admin/smtp/test', {
      method: 'POST',
      body: JSON.stringify({ email: testEmail }),
    });
  },

  // Email Templates
  getEmailTemplates: async (): Promise<{
    templates: Array<{
      id: number;
      name: string;
      subject: string;
      body: string;
      variables: string[];
      enabled: boolean;
      created_at: string;
      updated_at: string;
    }>;
  }> => {
    return apiRequest('/admin/email-templates');
  },

  getEmailTemplate: async (templateId: number): Promise<{
    template: {
      id: number;
      name: string;
      subject: string;
      body: string;
      variables: string[];
      enabled: boolean;
      created_at: string;
      updated_at: string;
    };
  }> => {
    return apiRequest(`/admin/email-templates/${templateId}`);
  },

  createEmailTemplate: async (data: {
    name: string;
    subject: string;
    body: string;
    variables: string[];
    enabled?: boolean;
  }): Promise<{
    message: string;
    template: {
      id: number;
      name: string;
      subject: string;
      body: string;
      variables: string[];
      enabled: boolean;
      created_at: string;
      updated_at: string;
    };
  }> => {
    return apiRequest('/admin/email-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateEmailTemplate: async (templateId: number, data: {
    subject?: string;
    body?: string;
    variables?: string[];
    enabled?: boolean;
  }): Promise<{ message: string }> => {
    return apiRequest(`/admin/email-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  toggleEmailTemplate: async (templateId: number): Promise<{
    message: string;
    enabled: boolean;
  }> => {
    return apiRequest(`/admin/email-templates/${templateId}/toggle`, {
      method: 'PUT',
    });
  },

  testEmailTemplate: async (templateId: number, email: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    return apiRequest(`/admin/email-templates/${templateId}/test`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Usage Statistics
  getUsageStats: async (): Promise<{
    statistics: {
      total: {
        translations: number;
        characters: number;
        documents: number;
        text: number;
      };
      last_7_days: {
        translations: number;
        characters: number;
        documents: number;
        text: number;
      };
      last_30_days: {
        translations: number;
        characters: number;
        documents: number;
        text: number;
      };
    };
  }> => {
    return apiRequest('/admin/usage/stats');
  },

  getUsageTimeline: async (period: '7days' | '30days' | '90days' = '7days'): Promise<{
    timeline: Array<{
      date: string;
      calls: number;
      translate: number;
      ocr: number;
      detect: number;
    }>;
  }> => {
    return apiRequest(`/admin/usage/timeline?period=${period}`);
  },

  getEndpointUsage: async (): Promise<{
    endpoints: Array<{
      endpoint: string;
      calls: number;
      percentage: number;
    }>;
  }> => {
    return apiRequest('/admin/usage/endpoints');
  },

  getTopUsers: async (): Promise<{
    top_users: Array<{
      user: string;
      calls: number;
      chars: number;
      ocr: number;
      docs: number;
      plan: string;
      rank: number;
    }>;
  }> => {
    return apiRequest('/admin/usage/top-users');
  },

  getAllUsersUsage: async (): Promise<{
    users: Array<{
      user: string;
      calls: number;
      chars: number;
      ocr: number;
      docs: number;
      plan: string;
      status: string;
    }>;
  }> => {
    return apiRequest('/admin/usage/users');
  },

  // Payment Gateways
  getPaymentGateways: async (): Promise<{
    gateways: {
      stripe: {
        enabled: boolean;
        production: {
          publishable_key: string;
          secret_key: string;
          webhook_secret: string;
        };
        sandbox: {
          publishable_key: string;
          secret_key: string;
          webhook_secret: string;
        };
        active_mode: 'sandbox' | 'production';
      };
      paypal: {
        enabled: boolean;
        production: {
          client_id: string;
          client_secret: string;
        };
        sandbox: {
          client_id: string;
          client_secret: string;
        };
        active_mode: 'sandbox' | 'production';
      };
      bank_transfer: {
        enabled: boolean;
        bank_name: string;
        account_name: string;
        account_number: string;
        iban: string;
        swift_bic: string;
        bank_address: string;
        instructions: string;
      };
      satim_dz: {
        enabled: boolean;
        production: {
          username: string;
          password: string;
          terminal_id: string;
          api_url: string;
        };
        sandbox: {
          username: string;
          password: string;
          terminal_id: string;
          api_url: string;
        };
        active_mode: 'sandbox' | 'production';
      };
      payoneer: {
        enabled: boolean;
        production: {
          client_id: string;
          client_secret: string;
          partner_id: string;
          api_url: string;
        };
        sandbox: {
          client_id: string;
          client_secret: string;
          partner_id: string;
          api_url: string;
        };
        active_mode: 'sandbox' | 'production';
      };
    };
  }> => {
    return apiRequest('/admin/payment-gateways');
  },

  updatePaymentGateway: async (gatewayName: 'stripe' | 'paypal' | 'bank_transfer' | 'satim_dz' | 'payoneer', data: any): Promise<{
    message: string;
    gateway: string;
  }> => {
    return apiRequest(`/admin/payment-gateways/${gatewayName}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  testPaymentGateway: async (gatewayName: 'stripe' | 'paypal'): Promise<{
    success: boolean;
    message: string;
    balance?: {
      available: Array<{ amount: number; currency: string }>;
      pending: Array<{ amount: number; currency: string }>;
    };
    token_info?: any;
    details?: string;
  }> => {
    return apiRequest(`/admin/payment-gateways/${gatewayName}/test`, {
      method: 'POST',
    });
  },

  // Billing Management
  getBillingTransactions: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    plan?: string;
    payment_method?: string;
    user_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    transactions: Array<{
      id: number;
      invoice_id: string;
      user: {
        id: number;
        name: string;
        email: string;
      };
      amount: number;
      currency: string;
      plan: string;
      status: string;
      payment_method?: string;
      stripe_payment_id?: string;
      satim_order_id?: string;
      description?: string;
      paid_at?: string;
      created_at: string;
    }>;
    pagination: {
      page: number;
      per_page: number;
      total: number;
      pages: number;
    };
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.plan) queryParams.append('plan', params.plan);
    if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return apiRequest(`/admin/billing/transactions${queryString ? `?${queryString}` : ''}`);
  },

  getBillingTransaction: async (transactionId: number): Promise<{
    transaction: {
      id: number;
      invoice_id: string;
      user: {
        id: number;
        name: string;
        email: string;
        plan: string;
      };
      amount: number;
      currency: string;
      plan: string;
      status: string;
      stripe_payment_id?: string;
      stripe_customer_id?: string;
      paid_at?: string;
      created_at: string;
      updated_at?: string;
    };
  }> => {
    return apiRequest(`/admin/billing/transactions/${transactionId}`);
  },

  refundTransaction: async (transactionId: number, reason: string): Promise<{
    success: boolean;
    message: string;
    refund_id?: string;
    transaction: {
      id: number;
      status: string;
      invoice_id: string;
    };
  }> => {
    return apiRequest(`/admin/billing/transactions/${transactionId}/refund`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  updateTransactionStatus: async (transactionId: number, status: string): Promise<{
    success: boolean;
    message: string;
    transaction: {
      id: number;
      status: string;
      invoice_id: string;
    };
  }> => {
    return apiRequest(`/admin/billing/transactions/${transactionId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  getBillingStatistics: async (period?: 'today' | 'week' | 'month' | 'year'): Promise<{
    statistics: {
      total_revenue: number;
      period_revenue: number;
      total_transactions: number;
      paid_transactions: number;
      failed_transactions: number;
      refunded_transactions: number;
    };
    plan_distribution: Record<string, number>;
    recent_transactions: Array<{
      id: number;
      invoice_id: string;
      user: string;
      amount: number;
      plan: string;
      paid_at?: string;
    }>;
    period: string;
  }> => {
    const queryParams = new URLSearchParams();
    if (period) queryParams.append('period', period);
    
    const queryString = queryParams.toString();
    return apiRequest(`/admin/billing/statistics${queryString ? `?${queryString}` : ''}`);
  },

  exportTransactions: async (params?: {
    status?: string;
    plan?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.plan) queryParams.append('plan', params.plan);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/admin/billing/export${queryString ? `?${queryString}` : ''}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to export transactions');
    }
    
    return response.blob();
  },

  downloadInvoice: async (transactionId: number): Promise<Blob> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/admin/billing/transactions/${transactionId}/invoice`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    
    return response.json();
  },

  // Bank Transfers (Admin)
  getBankTransfers: async (status?: string): Promise<{
    bank_transfers: Array<{
      id: number;
      user_id: number;
      reference: string;
      amount: number;
      currency: string;
      plan: string;
      billing_cycle: string;
      status: string;
      bank_name: string;
      account_name: string;
      account_number: string;
      iban: string;
      swift_bic: string;
      initiated_at?: string;
      verified_at?: string;
      completed_at?: string;
      notes?: string;
      created_at: string;
    }>;
  }> => {
    const query = status && status !== 'all' ? `?status=${status}` : '';
    return apiRequest(`/admin/bank-transfers${query}`);
  },

  verifyBankTransfer: async (transferId: number, action: 'verify' | 'reject' | 'complete', notes?: string): Promise<{
    success: boolean;
    bank_transfer: any;
  }> => {
    return apiRequest(`/admin/bank-transfers/${transferId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ action, notes: notes || '' }),
    });
  },
};

// Contact Messages API (Public)
export interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'unread' | 'read' | 'replied' | 'archived';
  admin_response?: string;
  responded_at?: string;
  responded_by?: number;
  created_at: string;
  updated_at?: string;
}

export interface ContactMessagesResponse {
  status: string;
  messages: ContactMessage[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export interface ContactMessageResponse {
  status: string;
  message: ContactMessage;
}

export interface ContactSubmitResponse {
  status: string;
  message: string;
  message_id: number;
}

// Contact API - Public endpoint
export const contactApi = {
  submitContact: async (data: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<ContactSubmitResponse> => {
    const response = await fetch(`${API_BASE_URL}/v1/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to submit contact form');
    }
    
    return response.json();
  },
};

// Admin Contact Messages API
export const adminContactApi = {
  getMessages: async (params?: {
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<ContactMessagesResponse> => {
    const token = getToken();
    
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    
    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/admin/contact-messages${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch contact messages');
    }
    
    return response.json();
  },

  getMessage: async (messageId: number): Promise<ContactMessageResponse> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/admin/contact-messages/${messageId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch contact message');
    }
    
    return response.json();
  },

  replyToMessage: async (messageId: number, response: string): Promise<{ status: string; message: string; email_errors?: string[] }> => {
    const token = getToken();
    
    const res = await fetch(`${API_BASE_URL}/admin/contact-messages/${messageId}/reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ response }),
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.message || 'Failed to send reply');
    }
    
    return data;
  },

  updateStatus: async (messageId: number, status: string): Promise<{ status: string; message: string }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/admin/contact-messages/${messageId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update message status');
    }
    
    return response.json();
  },

  deleteMessage: async (messageId: number): Promise<{ status: string; message: string }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/admin/contact-messages/${messageId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete message');
    }
    
    return response.json();
  },

  getStats: async (): Promise<{
    status: string;
    stats: {
      total: number;
      unread: number;
      read: number;
      replied: number;
      archived: number;
    };
  }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/admin/contact-messages/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch message stats');
    }
    
    return response.json();
  },
};

// WordPress Integration API
export interface WordPressIntegration {
  id: number;
  site_name: string;
  site_url: string;
  site_uuid: string;
  plugin_version: string;
  is_active: boolean;
  translations_count: number;
  translations_today: number;
  translations_this_month: number;
  last_translation_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WordPressAPIKey {
  id: number;
  api_key: string;
  label: string;
  usage_count: number;
  last_used: string | null;
  is_active: boolean;
  created_at: string;
}

export const wordpressApi = {
  // Get all WordPress integrations
  getIntegrations: async (): Promise<{ status: string; integrations: WordPressIntegration[] }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/integrations`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch WordPress integrations');
    }
    
    return response.json();
  },

  // Get a single integration
  getIntegration: async (integrationId: number): Promise<{ status: string; integration: WordPressIntegration }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/integrations/${integrationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch integration');
    }
    
    return response.json();
  },

  // Update an integration
  updateIntegration: async (integrationId: number, data: { site_name?: string; is_active?: boolean }): Promise<{ status: string; integration: WordPressIntegration }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/integrations/${integrationId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update integration');
    }
    
    return response.json();
  },

  // Delete an integration
  deleteIntegration: async (integrationId: number): Promise<{ status: string; message: string }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/integrations/${integrationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete integration');
    }
    
    return response.json();
  },

  // Get API keys for an integration
  getIntegrationKeys: async (integrationId: number): Promise<{ status: string; keys: WordPressAPIKey[] }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/integrations/${integrationId}/keys`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch API keys');
    }
    
    return response.json();
  },

  // Create a new API key
  createIntegrationKey: async (integrationId: number, label: string): Promise<{ status: string; api_key: string; key: WordPressAPIKey }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/integrations/${integrationId}/keys`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ label }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to create API key');
    }
    
    return response.json();
  },

  // Revoke an API key
  revokeKey: async (keyId: number): Promise<{ status: string; message: string }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/keys/${keyId}/revoke`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to revoke API key');
    }
    
    return response.json();
  },

  // Get WordPress stats
  getStats: async (): Promise<{
    status: string;
    stats: {
      total_sites: number;
      active_sites: number;
      total_translations: number;
      translations_today: number;
    };
  }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/wordpress/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    
    return response.json();
  },
};

// Shopify Integration API
export interface ShopifyIntegration {
  id: number;
  shop_name: string;
  shop_domain: string;
  shop_uuid: string;
  app_version: string;
  is_active: boolean;
  auto_translate_products: boolean;
  auto_translate_collections: boolean;
  default_source_lang: string;
  default_target_lang: string;
  translations_count: number;
  translations_today: number;
  translations_this_month: number;
  last_translation_at: string | null;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ShopifyAPIKey {
  id: number;
  api_key: string;
  label: string;
  usage_count: number;
  last_used: string | null;
  is_active: boolean;
  created_at: string;
}

export const shopifyApi = {
  // Get all Shopify integrations
  getIntegrations: async (): Promise<{ status: string; integrations: ShopifyIntegration[] }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/integrations`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch Shopify integrations');
    }
    
    return response.json();
  },

  // Get a single integration
  getIntegration: async (integrationId: number): Promise<{ status: string; integration: ShopifyIntegration }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/integrations/${integrationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch integration');
    }
    
    return response.json();
  },

  // Update an integration
  updateIntegration: async (integrationId: number, data: { shop_name?: string; is_active?: boolean; auto_translate_products?: boolean; auto_translate_collections?: boolean; default_source_lang?: string; default_target_lang?: string }): Promise<{ status: string; integration: ShopifyIntegration }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/integrations/${integrationId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update integration');
    }
    
    return response.json();
  },

  // Delete an integration
  deleteIntegration: async (integrationId: number): Promise<{ status: string; message: string }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/integrations/${integrationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete integration');
    }
    
    return response.json();
  },

  // Get API keys for an integration
  getIntegrationKeys: async (integrationId: number): Promise<{ status: string; keys: ShopifyAPIKey[] }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/integrations/${integrationId}/keys`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch API keys');
    }
    
    return response.json();
  },

  // Create a new API key
  createIntegrationKey: async (integrationId: number, label: string): Promise<{ status: string; api_key: string; key: ShopifyAPIKey }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/integrations/${integrationId}/keys`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ label }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to create API key');
    }
    
    return response.json();
  },

  // Revoke an API key
  revokeKey: async (keyId: number): Promise<{ status: string; message: string }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/keys/${keyId}/revoke`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to revoke API key');
    }
    
    return response.json();
  },

  // Get Shopify stats
  getStats: async (): Promise<{
    status: string;
    stats: {
      total_stores: number;
      active_stores: number;
      total_translations: number;
      translations_today: number;
    };
  }> => {
    const token = getToken();
    
    const response = await fetch(`${API_BASE_URL}/v1/shopify/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    
    return response.json();
  },
};

// Export token utilities
export { getToken, setTokens, clearTokens };
